import React, { useRef, useState } from "react";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { InputText } from "primereact/inputtext";
import { Dropdown } from "primereact/dropdown";
import { Toast } from "primereact/toast";
import { ConfirmDialog, confirmDialog } from "primereact/confirmdialog";
import { Tag } from "primereact/tag";
import {
  useLinkedInCompanyBlacklist,
  useCreateLinkedinCompanyBlacklistMutation,
  useDeleteLinkedinCompanyBlacklistMutation,
} from "../../../hooks/use-query-linkedincompanyblacklist";

const LinkedinBlacklistGrid: React.FC = () => {
  const toast = useRef<Toast>(null);
  const [showDialog, setShowDialog] = useState(false);
  const [companyName, setCompanyName] = useState("");
  const [matchType, setMatchType] = useState("CONTAINS");

  const { data: blacklistRes, isLoading } = useLinkedInCompanyBlacklist();
  const createMutation = useCreateLinkedinCompanyBlacklistMutation();
  const deleteMutation = useDeleteLinkedinCompanyBlacklistMutation();

  const blacklistData = blacklistRes?.data || [];

  const handleAdd = async () => {
    if (!companyName.trim()) {
      toast.current?.show({
        severity: "warn",
        summary: "Aviso",
        detail: "O nome da empresa é obrigatório.",
        life: 3000,
      });
      return;
    }

    try {
      await createMutation.mutateAsync({
        companyName: companyName.trim(),
        matchType,
        lastModifiedBy: "admin@nimber.com", // Em produção, obter do contexto de auth
      });

      toast.current?.show({
        severity: "success",
        summary: "Sucesso",
        detail: "Empresa adicionada à blacklist.",
        life: 3000,
      });

      setCompanyName("");
      setMatchType("CONTAINS");
      setShowDialog(false);
    } catch (err: any) {
      toast.current?.show({
        severity: "error",
        summary: "Erro",
        detail: err.message || "Erro ao adicionar empresa.",
        life: 3000,
      });
    }
  };

  const handleDelete = (id: string) => {
    confirmDialog({
      message: "Tem a certeza que deseja remover esta empresa da blacklist?",
      header: "Confirmação de Exclusão",
      icon: "pi pi-exclamation-triangle",
      acceptLabel: "Sim",
      rejectLabel: "Não",
      acceptClassName: "p-button-danger",
      accept: async () => {
        try {
          await deleteMutation.mutateAsync(id);
          toast.current?.show({
            severity: "success",
            summary: "Sucesso",
            detail: "Empresa removida da blacklist com sucesso.",
            life: 3000,
          });
        } catch (err: any) {
          toast.current?.show({
            severity: "error",
            summary: "Erro",
            detail: "Erro ao remover empresa da blacklist.",
            life: 3000,
          });
        }
      },
    });
  };

  const matchTypeTemplate = (rowData: any) => {
    const severity = rowData.matchType === "EXACT" ? "success" : "info";
    const label = rowData.matchType === "EXACT" ? "EXATO" : "CONTÉM";
    return <Tag severity={severity} value={label} />;
  };

  const actionsTemplate = (rowData: any) => {
    return (
      <Button
        icon="pi pi-trash"
        className="p-button-rounded p-button-danger p-button-text"
        onClick={() => handleDelete(rowData.id)}
        tooltip="Remover da Blacklist"
      />
    );
  };

  const header = (
    <div className="flex justify-content-between align-items-center flex-wrap gap-2">
      <h3 className="m-0">Blacklist de Empresas</h3>
      <Button
        label="Adicionar Empresa"
        icon="pi pi-plus"
        severity="success"
        onClick={() => setShowDialog(true)}
      />
    </div>
  );

  return (
    <div className="card">
      <Toast ref={toast} />
      <ConfirmDialog />

      <DataTable
        value={blacklistData}
        loading={isLoading}
        header={header}
        emptyMessage="Nenhuma empresa na blacklist."
        responsiveLayout="scroll"
        paginator
        rows={10}
      >
        <Column field="companyName" header="Nome da Empresa" sortable filter />
        <Column
          field="matchType"
          header="Tipo de Comparação"
          body={matchTypeTemplate}
          sortable
        />
        <Column
          header="Data de Cadastro"
          field="createdAt"
          body={(row) => new Date(row.createdAt).toLocaleDateString()}
          sortable
        />
        <Column body={actionsTemplate} header="Ações" style={{ width: "8rem", textAlign: "center" }} />
      </DataTable>

      <Dialog
        header="Adicionar Empresa à Blacklist"
        visible={showDialog}
        style={{ width: "450px" }}
        modal
        onHide={() => setShowDialog(false)}
        footer={
          <div>
            <Button
              label="Cancelar"
              icon="pi pi-times"
              className="p-button-text"
              onClick={() => setShowDialog(false)}
            />
            <Button
              label="Salvar"
              icon="pi pi-check"
              onClick={handleAdd}
              loading={createMutation.isPending}
            />
          </div>
        }
      >
        <div className="flex flex-column gap-3 mt-2">
          <div className="flex flex-column gap-1">
            <label htmlFor="companyName" className="font-bold">Nome da Empresa</label>
            <InputText
              id="companyName"
              value={companyName}
              onChange={(e) => setCompanyName(e.target.value)}
              placeholder="Ex: PrimeIT"
              className="w-full"
              autoFocus
            />
          </div>

          <div className="flex flex-column gap-1">
            <label htmlFor="matchType" className="font-bold">Tipo de Comparação</label>
            <Dropdown
              id="matchType"
              value={matchType}
              options={[
                { label: "Contém (Parcial)", value: "CONTAINS" },
                { label: "Exato (Igualdade)", value: "EXACT" },
              ]}
              onChange={(e) => setMatchType(e.value)}
              className="w-full"
            />
          </div>
        </div>
      </Dialog>
    </div>
  );
};

export default LinkedinBlacklistGrid;
